import {FormEvent, useContext, useEffect, useState} from "react";
import {Button} from "../atoms/button";
import classNames from "classnames";
import {LoadingSpinnerOverlay} from "../atoms/loading-spinner";
import {certificateSearch} from "../../lib/certificate-search";
import {Certificate, Dictionary} from "../../types/hygiena-types";
import {DrupalMedia} from "next-drupal";
import {DictionaryContext} from "../../context/dictionary-context";

/**
 * Certification search widget and functionality.
 */
export function WidgetCertificateSearch(): JSX.Element{
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certificate, setCertificate] = useState<DrupalMedia|undefined>(undefined);
  const [message, setMessage] = useState<string>("");
  const [lotNumber, setLotNumber] = useState("");
  const [loading, setLoading] = useState<boolean>();
  const [ready, setReady] = useState<boolean>();
  const t = useContext<Dictionary>(DictionaryContext);

  useEffect(() => {
    if (!ready) {
      setReady(true);
    }
    else {
      const hash = window.location.hash.replace("#", "");
      if (hash === "coa-search") {
        const element = document.getElementById("coa-search");
        if (element) element.scrollIntoView({behavior: "auto"});
      }
    }
  }, [ready]);

  /**
   * Gets and rendered the certificate by lot number.
   *
   * @param event
   */
  async function getCertificate(event: FormEvent) {
    event.preventDefault();
    await setLoading(true);

    const result = await certificateSearch(lotNumber);

    const certificate = result.data?.[0]?.file ?? null;
    setCertificate(certificate ?? undefined);
    setCertificates(result.data ?? []);
    if (result?.data?.length) {
      setMessage("");
    }
    else {
      if (!result?.error) setMessage(`${t?.all?.certificate_search_no_results} ${lotNumber}`);
      else setMessage(`${t?.all?.certificate_search_error}`);
    }
    await setLoading(false);
  }

  return (
    <div className="drop-shadow-sm bg-white p-12 w-full flex-grow relative scroll-m-60" id="coa-search">
      <LoadingSpinnerOverlay loading={loading} className="inset-0" spinnerClassName={"!top-0"} spinnerContainerClassName={"items-center"} />
      <form className="w-full" onSubmit={getCertificate}>
        <div className="flex flex-col mb-8">
          <label htmlFor="certificate-search--lot-number text-black">
            {`${t?.all?.lot_number}`}
          </label>
          <input onChange={(event) => setLotNumber(event?.target?.value ?? "")} value={lotNumber} type="text" id="certificate-search--lot-number" name="lot-number" placeholder={`${t?.all?.enter_a_lot_number}`} className="p-2 pl-0 border-b text-primary font-semibold bg-transparent" />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-col">
            <button className={classNames(
              "bg-primary text-white px-6 py-2 rounded transition-all duration-300 btn-certificate-search md:mb-8",
              {"opacity-50 pointer-events-none": !lotNumber?.length},
            )} disabled={!lotNumber?.length}>{`${t?.all?.search}`}</button>
            {message && (
              <p>{message}</p>
            )}
          </div>
        </div>
      </form>
      {certificates?.length ? (
        <div className="flex flex-col">
            {certificates.map((item, key) => {
              const brand = item?.product?.field_product_brand ? item?.product?.field_product_brand?.name : "";
              const suffix = item?.product?.field_product_brand?.field_brand_suffix ?? "";
              const product = item?.product?.title ?? "";
              const label = item?.label ?? "";
              let title = "";
              if (product) title = brand ? `${brand}${suffix} ${product}` : product;
              else if (label) title = label;
              return (
                <div className="mt-2 w-full" key={`coa-search--${key}`}>
                  <Button href={`/coas/${item.id}/${item.file}`}
                          height={20} width={20}
                          icon={"file"}
                          color={"primary"} hover={{color: "primary"}}
                          invert={true}
                          target={"_blank"}
                          containerClassName={"w-full"}
                          className="text-primary w-full text-sm flex !justify-start items-center gap-2 px-6 py-2 border-primary border-2 rounded-md font-semibold">
                    {title ? title : `${t?.all?.download_certificate}`}
                  </Button>
                </div>
              );
            })}
        </div>
      ): <></>}
    </div>
  );
}